import { createContext, useContext } from "react";
import type { MotionValue } from "motion/react";

export type Variant = "pill" | "underline" | "segment";
/** Whether trigger icons show on every tab or only on the selected one. */
export type IconDisplay = "always" | "selected";

type Ctx = {
  value: string;
  setValue: (v: string) => void;
  layoutId: string;
  variant: Variant;
  icons: IconDisplay;
  /** The pill's box in the list, for tabs whose width changes as they're selected. */
  track: Track;
  /** Whether the tracked pill has been measured; until then CSS places it. */
  placed: boolean;
  setPlaced: (placed: boolean) => void;
  /**
   * The one tab Tab reaches: the selected tab, or the first enabled one when
   * nothing enabled is selected. `null` until the triggers have registered,
   * when every enabled tab stays reachable.
   */
  tabStop: string | null;
  registerTab: (value: string, disabled: boolean) => void;
  unregisterTab: (value: string) => void;
  /** Values with a mounted panel, so triggers only point at panels that exist. */
  panels: ReadonlySet<string>;
  registerPanel: (value: string) => void;
  unregisterPanel: (value: string) => void;
  tabId: (value: string) => string;
  panelId: (value: string) => string;
};

type Track = Record<"x" | "y" | "width" | "height", MotionValue<number>>;

export const TabsCtx = createContext<Ctx | null>(null);

export function useTabs() {
  const ctx = useContext(TabsCtx);
  if (!ctx) throw new Error("Tabs.* must be used inside <Tabs>");
  return ctx;
}

/** Space between a selected-only icon and its label, in pixels. */
export const ICON_GAP = 6;
